import { computed, inject, Injectable } from '@angular/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import type { ChildOption } from '../../parent-ai-assistant/models/child-option.model';
import { FamilyHubService } from './family-hub.service';

const FAMILY_ADULT_ROLES = new Set(['PRIMARY', 'CO_PARENT', 'EXTENDED']);

function familyMemberRole(member: Pick<AuthGen.FamilyMember, 'role'>): string {
  return (member.role ?? '').trim().toUpperCase();
}

/** Kid roster only — never co-parents or caregivers. */
export function isFamilyChildMember(
  member: Pick<AuthGen.FamilyMember, 'role'>,
): boolean {
  const role = familyMemberRole(member);
  return role === 'CHILD' && !FAMILY_ADULT_ROLES.has(role);
}

/** Family CHILD member enriched for UI (names, avatar, challenge counts). */
export type FamilyChild = AuthGen.FamilyMember & {
  /** Alias of userId for selectors that expect `.id`. */
  id: string;
};

@Injectable({ providedIn: 'root' })
export class FamilyKidsService {
  #familyHub = inject(FamilyHubService);

  readonly family = computed(() => this.#familyHub.familyQuery.data());
  readonly isLoading = computed(() => this.#familyHub.familyQuery.isLoading());

  /** Active CHILD members from getFamily (source of truth for kid roster). */
  readonly children = computed<FamilyChild[]>(() => {
    const members = this.family()?.members ?? [];
    return members
      .filter((m) => isFamilyChildMember(m))
      .map((m) => ({
        ...m,
        id: m.userId,
      }));
  });

  /** Matches backend addKid seat check (active CHILD members only). */
  readonly activeChildren = computed(() =>
    this.children().filter((c) => (c.status ?? 'active') === 'active'),
  );

  readonly activeChildCount = computed(() => this.activeChildren().length);

  readonly hasChildren = computed(() => this.children().length > 0);

  readonly unverifiedChildren = computed(() =>
    this.children().filter((c) => !c.verified),
  );

  childById(userId: string): FamilyChild | undefined {
    return this.children().find((c) => c.userId === userId || c.id === userId);
  }

  displayName(child: Pick<AuthGen.FamilyMember, 'displayName' | 'firstName' | 'lastName' | 'userId'>): string {
    const fromParts = [child.firstName, child.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fromParts || child.displayName || child.userId;
  }

  toChildOptions(
    fallbackLabel: string,
  ): ChildOption[] {
    return this.children().map((c) => {
      const first = c.firstName ?? '';
      const last = c.lastName ?? '';
      const name = this.displayName(c) || fallbackLabel;
      const avatar = c.avatar?.trim();
      return {
        id: c.userId,
        name,
        avatarUrl: avatar || undefined,
        initialsData: {
          firstName: first || undefined,
          lastName: last || undefined,
        },
      };
    });
  }

  refetch(): Promise<unknown> {
    return this.#familyHub.familyQuery.refetch();
  }
}
